/**
 * Scenario Comparison Panel (Phase 3 — Step 3.2).
 *
 * Side-by-side comparison of 2–4 absence scenarios.
 */

import React, { useMemo } from 'react';
import { useAppStore } from '@/store';
import { simulateFutureAbsences } from '@abs/shared-logic';
import { computeTotalSessions } from '@abs/shared-logic';
import { RiskBadge, StatCard } from '../shared';
import type { Course, RiskLevel } from '@abs/shared-types';

interface ScenarioComparisonProps {
  course: Course;
}

interface Scenario {
  label: string;
  additionalAbsences: number;
  percentage: string;
  grade: string | null;
  risk: RiskLevel;
  autoFail: boolean;
}

export function ScenarioComparison({ course }: ScenarioComparisonProps): React.ReactElement {
  const store = useAppStore;

  const scenarios = useMemo(() => {
    const state = store.getState();
    const policy = state.policies.find((p) => p.id === course.policyId);
    if (!policy) return [];

    const entries = state.entries.filter((e) => e.courseId === course.id);
    const { total } = computeTotalSessions(
      course.meetingPatterns, course.sessionOverrides,
      course.startDate, course.endDate,
    );

    const configs = [
      { label: 'Miss 0', count: 0 },
      { label: 'Miss 1', count: 1 },
      { label: 'Miss 2', count: 2 },
      { label: 'Miss 3', count: 3 },
    ];

    return configs.map(({ label, count }): Scenario => {
      const sim = simulateFutureAbsences(course, policy, entries, total, count, course.baseGrade);
      return {
        label,
        additionalAbsences: count,
        percentage: sim.projectedPercentage.toFixed(1),
        grade: sim.projectedGrade,
        risk: sim.projectedRiskLevel,
        autoFail: sim.isAutoFail,
      };
    });
  }, [course, store]);

  if (scenarios.length === 0) {
    return <p>No policy configured for this course.</p>;
  }

  return (
    <div className="scenario-comparison" role="region" aria-label="Scenario Comparison">
      <h4>What happens if you miss more sessions?</h4>
      <p className="stat-caption">Each card projects attendance, grade, and risk if that many additional unexcused absences are incurred from your current state.</p>
      <div className="scenario-comparison__grid">
        {scenarios.map((s) => (
          <div
            key={s.label}
            className={`scenario-comparison__card ${s.autoFail ? 'scenario-comparison__card--fail' : ''}`}
          >
            <h5>{s.label}</h5>
            <StatCard value={`${s.percentage}%`} label="Attendance" />
            {s.grade && (
              <StatCard value={s.grade} label="Grade" />
            )}
            <RiskBadge level={s.risk} />
            {s.autoFail && <div className="scenario-comparison__auto-fail">AUTO-FAIL</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
